import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const Section = ({ title, children, action }) => (
  <section className="rounded-lg border border-stone-200 bg-white">
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 p-4"><h2 className="font-semibold">{title}</h2>{action}</div>
    <div className="p-4">{children}</div>
  </section>
);

const TAX_PRESETS = [
  { label: "PPN 12%", name: "PPN", rate: 12 },
  { label: "PPN 11%", name: "PPN", rate: 11 },
  { label: "PPh Final UMKM 0,5%", name: "Pajak E-commerce (PPh Final)", rate: 0.5 },
];

const TaxSection = ({ tax, onChange }) => (
  <Section title="Pajak E-commerce">
    <div className="mb-4 flex flex-wrap gap-2">
      {TAX_PRESETS.map((p) => (
        <Button key={p.label} type="button" variant="outline" size="sm" data-testid={`tax-preset-${p.rate}`}
          onClick={() => onChange({ ...tax, name: p.name, rate_percent: p.rate })}>{p.label}</Button>
      ))}
    </div>
    <div className="grid gap-4 md:grid-cols-2">
      <label className="flex items-center gap-2 text-sm md:col-span-2"><Switch checked={tax.enabled} onCheckedChange={(v) => onChange({ ...tax, enabled: v })} data-testid="tax-enabled-switch" /> Aktifkan pajak</label>
      <div className="space-y-1.5"><Label>Nama pajak</Label><Input data-testid="tax-name-input" value={tax.name} onChange={(e) => onChange({ ...tax, name: e.target.value })} /></div>
      <div className="space-y-1.5"><Label>Tarif (%)</Label><Input type="number" step="0.01" min="0" data-testid="tax-rate-input" value={tax.rate_percent} onChange={(e) => onChange({ ...tax, rate_percent: Number(e.target.value) })} /></div>
      <div className="space-y-1.5"><Label>Berlaku mulai</Label><Input type="date" data-testid="tax-date-input" value={tax.effective_date} onChange={(e) => onChange({ ...tax, effective_date: e.target.value })} /></div>
      <label className="flex items-center gap-2 self-end pb-2 text-sm"><Switch checked={tax.charge_to_buyer} onCheckedChange={(v) => onChange({ ...tax, charge_to_buyer: v })} data-testid="tax-buyer-switch" /> Tambahkan ke tagihan pembeli</label>
    </div>
    <p className="mt-3 text-xs text-stone-500">Pajak otomatis tampil dan dihitung di checkout pelanggan mulai tanggal berlaku. Jika ditagihkan ke pembeli, pajak ditambahkan ke total; jika tidak, checkout menampilkan keterangan harga sudah termasuk pajak dan pajak dicatat sebagai beban toko (mengurangi laba bersih). Dihitung dari subtotal produk (tanpa ongkir).</p>
  </Section>
);

const RatesTable = ({ partner, onChange }) => {
  const setRate = (i, k, v) => onChange({ ...partner, rates: partner.rates.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const removeRate = (i) => window.confirm(`Hapus tarif tujuan "${partner.rates[i].destination || "baru"}"?`) && onChange({ ...partner, rates: partner.rates.filter((_, j) => j !== i) });
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[620px] text-sm">
        <thead className="bg-stone-100 text-left text-xs text-stone-600"><tr><th className="p-2">Tujuan (kota/provinsi)</th><th className="p-2">Provinsi</th><th className="p-2 text-right">Reguler (Rp/kg)</th><th className="p-2 text-right">Ekonomis (Rp/kg)</th><th className="p-2" /></tr></thead>
        <tbody className="divide-y divide-stone-100">
          {partner.rates.map((r, i) => (
            <tr key={i}>
              <td className="p-1.5"><Input className="h-8" value={r.destination} onChange={(e) => setRate(i, "destination", e.target.value)} data-testid={`rate-dest-${partner.id}-${i}`} /></td>
              <td className="p-1.5"><Input className="h-8" value={r.province} onChange={(e) => setRate(i, "province", e.target.value)} /></td>
              <td className="p-1.5"><Input type="number" min="0" className="tnum h-8 text-right" value={r.reguler} onChange={(e) => setRate(i, "reguler", Number(e.target.value))} data-testid={`rate-reg-${partner.id}-${i}`} /></td>
              <td className="p-1.5"><Input type="number" min="0" className="tnum h-8 text-right" value={r.ekonomis} onChange={(e) => setRate(i, "ekonomis", Number(e.target.value))} data-testid={`rate-eko-${partner.id}-${i}`} /></td>
              <td className="p-1.5"><Button variant="ghost" size="icon" data-testid={`rate-remove-${partner.id}-${i}`} onClick={() => removeRate(i)}><Trash2 className="h-4 w-4 text-red-700" /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="outline" size="sm" className="mt-2" data-testid={`add-rate-${partner.id}`} onClick={() => onChange({ ...partner, rates: [...partner.rates, { destination: "", province: "", reguler: 0, ekonomis: 0 }] })}><Plus className="mr-1 h-4 w-4" />Tambah tujuan</Button>
      <p className="mt-2 text-xs text-stone-500">Isi 0 jika layanan tidak tersedia untuk tujuan tersebut.</p>
    </div>
  );
};

const PartnerCard = ({ partner, onChange, onRemove }) => {
  const set = (k) => (e) => onChange({ ...partner, [k]: e.target.value });
  return (
    <div className="space-y-4 rounded-lg border border-stone-200 p-4" data-testid={`partner-card-${partner.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={partner.active} onCheckedChange={(v) => onChange({ ...partner, active: v })} data-testid={`partner-active-${partner.id}`} /> Dipakai</label>
        <Button variant="ghost" size="sm" className="ml-auto text-red-700" onClick={onRemove} data-testid={`partner-remove-${partner.id}`}><Trash2 className="mr-1 h-4 w-4" />Hapus mitra</Button>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <div className="space-y-1"><Label className="text-xs">Nama mitra</Label><Input value={partner.name} onChange={set("name")} data-testid={`partner-name-${partner.id}`} /></div>
        <div className="space-y-1"><Label className="text-xs">Website</Label><Input value={partner.website} placeholder="https://" onChange={set("website")} /></div>
        <div className="space-y-1"><Label className="text-xs">Estimasi Reguler</Label><Input value={partner.etd_reguler} onChange={set("etd_reguler")} /></div>
        <div className="space-y-1"><Label className="text-xs">Estimasi Ekonomis</Label><Input value={partner.etd_ekonomis} onChange={set("etd_ekonomis")} /></div>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={partner.per_gram_under_1kg} onCheckedChange={(v) => onChange({ ...partner, per_gram_under_1kg: v })} /> Paket di bawah 1 kg dihitung per gram</label>
      <RatesTable partner={partner} onChange={onChange} />
    </div>
  );
};

const TITLES = { tarif: "Kelola Tarif Pengiriman", pajak: "Pajak E-commerce", toko: "Lokasi Toko & Kontak" };

export default function AdminShipping({ section = "tarif" }) {
  const call = useApi();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { call("get", "/admin/settings").then(setS); }, [call]);
  if (!s) return <p className="text-sm text-stone-500">Memuat...</p>;

  const setPartner = (i, p) => setS({ ...s, partners: s.partners.map((x, j) => (j === i ? p : x)) });
  const addPartner = () => setS({ ...s, partners: [...s.partners, { id: `mitra${Date.now()}`, name: "Mitra Baru", website: "", active: true, per_gram_under_1kg: false, etd_reguler: "", etd_ekonomis: "", rates: [] }] });

  const save = async () => {
    setSaving(true);
    try { setS(await call("put", "/admin/settings", s)); toast.success("Pengaturan disimpan"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={TITLES[section]}>
        <Button onClick={save} disabled={saving} data-testid="shipping-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan Perubahan</Button>
      </PageHeader>
      {section === "toko" && <Section title="Lokasi Toko & Kontak">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2"><Label>Alamat toko / titik pengiriman</Label><Input data-testid="settings-address" value={s.address} onChange={(e) => setS({ ...s, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Kota asal</Label><Input data-testid="settings-origin" value={s.origin_city} onChange={(e) => setS({ ...s, origin_city: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>WhatsApp admin (format 62...)</Label><Input data-testid="settings-admin-wa" value={s.admin_whatsapp} onChange={(e) => setS({ ...s, admin_whatsapp: e.target.value })} /></div>
        </div>
      </Section>}
      {section === "tarif" && <Section title="Mitra Pengiriman (layanan Reguler & Ekonomis)" action={<Button variant="outline" size="sm" data-testid="add-partner-button" onClick={addPartner}><Plus className="mr-1 h-4 w-4" />Tambah Mitra</Button>}>
        <p className="mb-4 text-sm text-stone-600">Pembeli hanya melihat dua pilihan per mitra: Reguler dan Ekonomis. Ongkir = tarif per kg × berat (dibulatkan ke atas per kg), atau per gram untuk paket di bawah 1 kg jika opsi itu aktif.</p>
        <div className="space-y-4">
          {s.partners.map((p, i) => <PartnerCard key={p.id} partner={p} onChange={(np) => setPartner(i, np)} onRemove={() => window.confirm(`Hapus mitra ${p.name}?`) && setS({ ...s, partners: s.partners.filter((_, j) => j !== i) })} />)}
          {s.partners.length === 0 && <p className="text-sm text-stone-500">Belum ada mitra pengiriman. Tambahkan minimal satu agar pembeli bisa checkout.</p>}
        </div>
      </Section>}
      {section === "pajak" && <TaxSection tax={s.tax} onChange={(tax) => setS({ ...s, tax })} />}
    </div>
  );
}
